import makeApiCall from "../master/axiosIndex";
import type {
  CreateTenderRequest,
  UpdateTenderRequest,
  FetchTendersQueryParams,
  ApprovePublishTenderRequest,
  RejectPublishTenderRequest,
  ApproveEvaluationTenderRequest,
  RejectEvaluationTenderRequest,
} from "../types/tender";

const tenderApis = {
  createTender: (data: CreateTenderRequest, files?: File[]): Promise<any> => {
    const formData = new FormData();
    formData.append("tender", JSON.stringify(data));
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file);
      });
    }
    return makeApiCall<any>({
      method: "post",
      url: "/tms/tender/create",
      data: formData,
    });
  },

  updateTender: (data: UpdateTenderRequest, files?: File[]): Promise<any> => {
    const formData = new FormData();
    formData.append("tender", JSON.stringify(data));
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file);
      });
    }
    return makeApiCall<any>({
      method: "put",
      url: "/tms/tender/update",
      data: formData,
    });
  },

  fetchTenders: (params: FetchTendersQueryParams): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: "/tms/tender/list",
      params,
    });
  },

  getTenderById: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/tender/${tenderId}`,
    });
  },

  approvePublish: (
    tenderId: number,
    data: ApprovePublishTenderRequest,
    files?: File[],
  ): Promise<any> => {
    const formData = new FormData();
    if (data.remarks) {
      formData.append("remarks", data.remarks);
    }
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file);
      });
    }
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/approve-publish/${tenderId}`,
      data: formData,
    });
  },

  rejectPublish: (
    tenderId: number,
    data: RejectPublishTenderRequest,
    files?: File[],
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("reason", data.reason);
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file);
      });
    }
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/reject-publish/${tenderId}`,
      data: formData,
    });
  },

  approveEvaluation: (
    tenderId: number,
    data: ApproveEvaluationTenderRequest,
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/approve-evaluation/${tenderId}`,
      data,
    });
  },

  rejectEvaluation: (
    tenderId: number,
    data: RejectEvaluationTenderRequest,
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/reject-evaluation/${tenderId}`,
      data,
    });
  },

  fetchBidResponses: (tenderId: number, params: any): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/tender/bid-responses/${tenderId}`,
      params,
    });
  },

  getTenderWithBid: (
    tenderId: number,
    bidId: number,
    bidType: "Supplier" | "Contractor",
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/tender/tender-with-bid/${tenderId}/${bidId}`,
      params: { bidType },
    });
  },

  publishTender: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/publish/${tenderId}`,
    });
  },

  getTenderEvaluationResults: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/evaluation/tender-results/${tenderId}`,
    });
  },

  adjustScore: (
    tenderId: number,
    data: {
      bidId: number;
      originalScore: number;
      adjustedScore: number;
      reason?: string;
    },
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/evaluation/adjust-score/${tenderId}`,
      data,
    });
  },

  fetchBafoBidResponses: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/tender/bafo-bid-responses/${tenderId}`,
    });
  },

  // Allocation APIs
  createAllocation: (data: {
    tenderId: number;
    allocations: {
      tenderMaterialId?: number;
      tenderTaskId?: number;
      supplierBidId?: number;
      contractorBidId?: number;
      awardQty: number;
      reason?: string;
    }[];
  }): Promise<any> => {
    return makeApiCall<any>({
      method: "post",
      url: "/tms/allocation/create",
      data,
    });
  },

  fetchTenderAllocations: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/allocation/tender/${tenderId}`,
    });
  },

  updateAllocation: (
    allocationId: number,
    data: { awardQty: number; reason?: string },
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/allocation/update/${allocationId}`,
      data,
    });
  },

  deleteAllocation: (allocationId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "delete",
      url: `/tms/allocation/delete/${allocationId}`,
    });
  },

  fetchSectionScores: (params: {
    tenderId: number;
    bidId: number;
    bidType: string;
    sectionId: number;
    evaluatorId: number;
  }): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: "/tms/evaluation/section-scores",
      params,
    });
  },

  submitSectionEvaluation: (data: {
    tenderId: number;
    bidId: number;
    bidType: string;
    sectionId: number;
    evaluatorId: number;
    questionScores: {
      questionId: number;
      score: number;
      remarks: string;
      comments: string;
    }[];
    sectionRemarks: string;
    evaluatorComments: string;
  }): Promise<any> => {
    return makeApiCall<any>({
      method: "post",
      url: "/tms/evaluation/submit-section",
      data,
    });
  },
  generatePublicLink: (data: {
    tenderId: number;
    expiresInDays: number;
    settings: string;
  }): Promise<any> => {
    return makeApiCall<any>({
      method: "post",
      url: "/tms/tender/generate-public-link",
      data,
    });
  },

  applyDiscount: (
    tenderId: number,
    supplierBidId: number,
    data: {
      materialDiscounts: { bidMaterialId: number; discountPercent: number }[];
      revisionReason: string;
    },
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/apply-discount/${tenderId}/${supplierBidId}`,
      data,
    });
  },

  addTargetPrice: (data: {
    tenderId: number;
    tenderFor: string;
    lines: { lineId: number; targetPrice: number }[];
  }): Promise<any> => {
    const { tenderId, ...body } = data;
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/target-price/${tenderId}`,
      data: body,
    });
  },

  savePriceComparisonIndicators: (
    tenderId: number,
    indicators: { title: string; color: string; startPrice: number; endPrice: number }[]
  ): Promise<any> => {
    return makeApiCall<any>({
      method: "put",
      url: `/tms/tender/price-comparison-indicators/${tenderId}`,
      data: { indicators },
    });
  },

  fetchPriceComparisonIndicators: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "get",
      url: `/tms/tender/price-comparison-indicators/${tenderId}`,
    });
  },

  fetchBidVersions: (tenderId: number): Promise<any> => {
    return makeApiCall<any>({
      method: "post",
      url: "/tms/tender/bid-versions",
      data: { tenderId },
    });
  },

  bulkUploadValidate: (data: FormData): Promise<any> =>
    makeApiCall<any>({
      method: "post",
      url: "/tms/tender-bulk-upload/validate",
      data,
    }),

  validateDiscountBulkUpload: (data: FormData): Promise<any> =>
    makeApiCall<any>({
      method: "post",
      url: "/tms/tender-bulk-upload/validate-discount",
      data,
    }),

  validateTargetPriceBulkUpload: (data: FormData): Promise<any> =>
    makeApiCall<any>({
      method: "post",
      url: "/tms/tender-bulk-upload/validate-target-price",
      data,
    }),
};

export default tenderApis;
